/**
 * Google Address Autocomplete Directive (Attribute)
 * Uses the legacy Autocomplete API for simple, clean integration
 * 
 * Usage:
 *   <input 
 *     type="text" 
 *     ng-model="address" 
 *     google-address-autocomplete
 *     on-place-selected="handlePlaceSelected(place)">
 *   </input>
 * 
 * With options:
 *   <input 
 *     type="text" 
 *     ng-model="address" 
 *     google-address-autocomplete="{types: ['address'], componentRestrictions: {country: 'us'}}"
 *     on-place-selected="handlePlaceSelected(place)">
 *   </input>
 * 
 * Options:
 *   - types: Array of place types (default: ['address'])
 *   - componentRestrictions: Object to restrict results (e.g., {country: 'us'})
 * 
 * Callback:
 *   - on-place-selected: Function called when a place is selected, receives {place: place} object
 */
function googleAddressAutocompleteDirective($window, $timeout) {
  "ngInject";

  return {
    restrict: 'A',
    require: 'ngModel',
    scope: {
      googleAddressAutocomplete: '=?', // Optional options object
      onPlaceSelected: '&?', // Optional callback when place is selected
    },
    link: function(scope, element, attrs, ngModel) {
      let autocomplete = null;

      // Check if Google Maps API is already loaded
      function checkGoogleMaps() {
        if ($window.google && $window.google.maps && $window.google.maps.places) {
          initializeAutocomplete();
        } else {
          // Wait a bit and check again
          $timeout(checkGoogleMaps, 100);
        }
      }

      // Initialize the autocomplete
      function initializeAutocomplete() {
        if (autocomplete) {
          return;
        }

        const options = scope.googleAddressAutocomplete || {};
        const inputElement = element[0];
        
        // Use the legacy Autocomplete API (simple and reliable)
        const autocompleteOptions = {
          types: options.types || ['address'],
          componentRestrictions: options.componentRestrictions || null,
        };

        // Create the autocomplete instance
        autocomplete = new $window.google.maps.places.Autocomplete(inputElement, autocompleteOptions);

        // Listen for place selection
        autocomplete.addListener('place_changed', function() {
          const place = autocomplete.getPlace();
          
          if (!place.geometry) {
            // User entered something that wasn't selected from the dropdown
            // Just update the model with what they typed
            scope.$apply(function() {
              ngModel.$setViewValue(inputElement.value);
              ngModel.$render();
            });
            return;
          }

          // Update the ngModel with the formatted address
          scope.$apply(function() {
            ngModel.$setViewValue(place.formatted_address || inputElement.value);
            ngModel.$render();
          });

          // Call optional callback
          if (scope.onPlaceSelected) {
            scope.onPlaceSelected({ place: place });
          }
        });

        // Sync input changes to ngModel (for manual typing)
        inputElement.addEventListener('input', function() {
          if (scope.$$phase || scope.$root.$$phase) {
            ngModel.$setViewValue(inputElement.value);
            ngModel.$render();
          } else {
            scope.$apply(function() {
              ngModel.$setViewValue(inputElement.value);
              ngModel.$render();
            });
          }
        });
      }

      // Watch for changes to options
      scope.$watch('googleAddressAutocomplete', function(newOptions) {
        if (autocomplete && newOptions) {
          // Reinitialize if options change
          if ($window.google && $window.google.maps) {
            $window.google.maps.event.clearInstanceListeners(autocomplete);
          }
          autocomplete = null;
          initializeAutocomplete();
        }
      }, true);

      // Start checking for Google Maps API
      checkGoogleMaps();

      // Cleanup on destroy
      scope.$on('$destroy', function() {
        if (autocomplete && $window.google && $window.google.maps) {
          // Clear all event listeners
          $window.google.maps.event.clearInstanceListeners(autocomplete);
          autocomplete = null;
        }
      });
    }
  };
}

export default ['$window', '$timeout', googleAddressAutocompleteDirective];



// WEBPACK FOOTER //
// ./client/common/directives/google-address-autocomplete/google-address-autocomplete.component.js